import { pendingTabFocusAtomFamily, type Tab } from '@/atoms/sessionWindowsAtoms';
import { TabType } from '@/container/ScribeTabs/index.v2.types';
import { useIsNewSessionListEnabled } from '@/features/flags';
import { useIsWorkHomeObjectWorkspaceEnabled } from '@/features/flags/work-home-object-workspace-flag';
import { usePrefetchSessionById } from '@/hooks/query/useQuerySessionById';
import {
  getChatHref,
  getWorkHomeObjectHref,
  parseWorkHomeObjectRoute,
  WORK_CHAT_HOME_PATH,
} from '@heidi/agents/contracts';
import { useResetProjectsComposerContext } from '@heidi/agents/data';
import type { SessionListItem } from '@heidi/session/contracts';
import { useStore } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useMobilePanel } from '../../mobile/use-mobile-panel';
import { trackNavV3ButtonClicked } from '../nav-v3-analytics';
import { workHomeObjectEntryGateAtom } from '../object-workspace/object-workspace-navigation';

/**
 * Navigation for the Recent Visits rows and their fragment cluster.
 *
 * Session tabs are not URL-addressable, so Context/Note fragments use the
 * established atom-then-navigate idiom: seed `pendingTabFocusAtomFamily`
 * (consumed by `useSessionWindowsSync` on the V2 session page, which focuses
 * the tab once it lands and clears the intent) and then push the session
 * route. On the legacy session page the intent is simply never consumed and
 * the session opens at its default tab — graceful degradation.
 *
 * Ask-Heidi threads are real chats with real URLs (`getChatHref`), so they
 * navigate directly into the full chat experience.
 *
 * When the object-workspace kill switch is on, the row's primary open lands
 * on the visit-scoped Work Home URL instead of expanding the fragment cluster.
 */
export function useOpenSessionFragment() {
  const router = useRouter();
  const store = useStore();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isObjectWorkspaceEnabled = useIsWorkHomeObjectWorkspaceEnabled();
  const prefetchSessionById = usePrefetchSessionById();
  const { dismissMobileSidebar } = useMobilePanel();
  const resetProjectsComposerContext = useResetProjectsComposerContext();
  const activeObjectRoute = isObjectWorkspaceEnabled
    ? parseWorkHomeObjectRoute(router.query)
    : null;

  const getSessionHref = useCallback(
    (sessionId: string) =>
      isNewSessionListEnabled
        ? `/scribe/sessions/${sessionId}`
        : `/scribe/session/${sessionId}`,
    [isNewSessionListEnabled]
  );

  /** Warm the detail cache on hover. Offline-synthetic rows have no backend
   *  record — a prefetch would 404 and poison `notFoundSessionIds`. */
  const prefetchVisit = useCallback(
    (session: Pick<SessionListItem, 'session_id' | 'is_offline_synthetic'>) => {
      if (!session.is_offline_synthetic) {
        prefetchSessionById(session.session_id);
      }
    },
    [prefetchSessionById]
  );

  const openVisitWorkspace = useCallback(
    (session: Pick<SessionListItem, 'session_id' | 'is_offline_synthetic'>) => {
      if (!isObjectWorkspaceEnabled) {
        return Promise.resolve(false);
      }
      trackNavV3ButtonClicked({
        label: 'nav_v3_recent_visit',
        session_id: session.session_id,
      });
      prefetchVisit(session);
      dismissMobileSidebar();
      const href = getWorkHomeObjectHref({ kind: 'visit', id: session.session_id });
      if (router.pathname !== `${WORK_CHAT_HOME_PATH}/[[...id]]`) {
        // Work's mount reset clears composer context on arrival.
        return router.push(href);
      }
      // A shallow same-page open keeps the composer atoms, so clear the
      // previous context chip here, as in-scene openObjectWorkspace does.
      // The guard never holds a re-open of the current visit, so keep its context.
      const isAlreadyOpen =
        activeObjectRoute?.kind === 'visit' &&
        activeObjectRoute.id === session.session_id;
      if (
        !isAlreadyOpen &&
        !store.get(workHomeObjectEntryGateAtom)?.deferResetIfHeld(href)
      ) {
        resetProjectsComposerContext();
      }
      return router.push(href, undefined, { shallow: true });
    },
    [
      activeObjectRoute,
      dismissMobileSidebar,
      isObjectWorkspaceEnabled,
      prefetchVisit,
      resetProjectsComposerContext,
      router,
      store,
    ]
  );

  const openSessionTab = useCallback(
    (sessionId: string, tab: Tab) => {
      // Only the new session page consumes this atom. Avoid leaving a stale
      // focus intent behind when nav-v3 is paired with the legacy session route.
      if (isNewSessionListEnabled) {
        store.set(pendingTabFocusAtomFamily(sessionId), tab);
      }
      dismissMobileSidebar();
      void router.push(getSessionHref(sessionId));
    },
    [store, isNewSessionListEnabled, dismissMobileSidebar, router, getSessionHref]
  );

  /** The session at its default landing page — no tab intent seeded, so the
   *  page opens wherever it would open from any other entry point. */
  const openSession = useCallback(
    (sessionId: string) => {
      dismissMobileSidebar();
      void router.push(getSessionHref(sessionId));
    },
    [dismissMobileSidebar, router, getSessionHref]
  );

  const openContextTab = useCallback(
    (sessionId: string) => {
      trackNavV3ButtonClicked({
        label: 'nav_v3_session_fragment',
        click_source: 'context',
        session_id: sessionId,
      });
      openSessionTab(sessionId, { name: TabType.CONTEXT });
    },
    [openSessionTab]
  );

  const openNoteTab = useCallback(
    (sessionId: string) => {
      trackNavV3ButtonClicked({
        label: 'nav_v3_session_fragment',
        click_source: 'note',
        session_id: sessionId,
      });
      openSessionTab(sessionId, { name: TabType.NOTE });
    },
    [openSessionTab]
  );

  const openChat = useCallback(
    (chat: { id: string; version?: 1 | 2; session_id?: string | null }) => {
      trackNavV3ButtonClicked({
        label: 'nav_v3_session_fragment',
        click_source: 'chat',
        chat_id: chat.id,
        session_id: chat.session_id ?? undefined,
      });
      dismissMobileSidebar();
      void router.push(getChatHref(chat));
    },
    [dismissMobileSidebar, router]
  );

  return {
    activeObjectRoute,
    openVisitWorkspace,
    openSession,
    openContextTab,
    openNoteTab,
    openChat,
    prefetchVisit,
  };
}
